import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  SelectionCriterionDto,
  SelectionCriterionType,
} from "../../../../../autogen/bff-api";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { SELECTION_CRITERION_TYPE_OPTIONS } from "../utils/publicProcurementOptions";

// t('Add requirement') t('Edit requirement') t('Type') t('Requirement title')
// t('Qualification requirement') t('Requires documentation') t('Cancel') t('Save')
// t('Type is required') t('Title is required') t('Choose a type')

export interface QualificationFormValues {
  type: SelectionCriterionType | "";
  name: string;
  description: string;
  requiresEvidence: boolean;
}

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  mode: "add" | "edit";
  defaultValues?: QualificationFormValues;
  onSubmit: (values: SelectionCriterionDto) => Promise<void>;
  onClose: () => void;
}

const emptyDefaults: QualificationFormValues = {
  type: "",
  name: "",
  description: "",
  requiresEvidence: false,
};

export const QualificationRequirementDrawer = ({
  isOpen,
  isSaving,
  mode,
  defaultValues: defaultValuesProp,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<QualificationFormValues>({
    defaultValues: defaultValuesProp ?? emptyDefaults,
  });

  const submitHandler = handleSubmit(async (values) => {
    if (!values.type) return;
    const type = values.type;
    await onSubmit({
      type,
      name: values.name,
      description: values.description,
      requiresEvidence: values.requiresEvidence,
    });
  });

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      closeOnOverlayClick={!isSaving}
      size="md"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton isDisabled={isSaving} />
        <DrawerHeader>{t(mode === "edit" ? "Edit requirement" : "Add requirement")}</DrawerHeader>

        <DrawerBody>
          <form id="qualification-form" data-testid="qualification-form" onSubmit={submitHandler}>
            <FormControl mb="6" isInvalid={errors.type !== undefined}>
              <FormLabel>{t("Type")}</FormLabel>
              <Select
                {...register("type", {
                  required: t("Type is required") ?? "",
                })}
                borderColor={errors.type ? "smError" : "smBorder"}
              >
                <option value="">{t("Choose a type")}</option>
                {SELECTION_CRITERION_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>
                {errors.type?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl mb="6" isRequired isInvalid={errors.name !== undefined}>
              <FormLabel>{t("Title")}</FormLabel>
              <Input
                placeholder={t("Requirement title") ?? ""}
                {...register("name", {
                  required: t("Title is required") ?? "",
                })}
              />
              <FormErrorMessage>
                {errors.name?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl mb="6">
              <FormLabel>{t("Qualification requirement")}</FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TipTap
                    content={value}
                    onUpdate={onChange}
                    editable={true}
                    toolbar={{ underline: false, headings: false, colors: false, link: false, table: false, image: false, template: false }}
                    debounceWait={0}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <Controller
                name="requiresEvidence"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    isChecked={value}
                    onChange={onChange}
                    colorScheme="teal"
                  >
                    {t("Requires documentation")}
                  </Checkbox>
                )}
              />
            </FormControl>
          </form>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="smBorder">
          <Flex gap="3">
            <Button
              variant="outline"
              onClick={onClose}
              isDisabled={isSaving}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              form="qualification-form"
              colorScheme="teal"
              isLoading={isSaving}
            >
              {t("Save")}
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
